<template>
  <VueEditor
    v-model="content"
    ref="editorRef"
    :id="id"
    :editor-options="toolbarOptions"
    @text-change="updateValue"
  />
</template>

<script setup lang="ts">
import { ref, shallowRef } from "vue"
import { VueEditor } from "vue2-editor"

type Props = {
  value?: string
  id?: string
}
type Emits = {
  (e: "update:modelValue" | "change" | "input", value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  id: "textEditor"
})
const emit = defineEmits<Emits>()

const editorRef = ref()
const content = shallowRef(props.value || "")

const updateValue = () => {
  let html = editorRef.value.quill.getHTML()

  emit("change", html)
  emit("input", html)
  emit("update:modelValue", html)
}

const toolbarOptions = {
  modules: {
    toolbar: {
      container: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ["bold", "italic"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["clean"]
      ]
    }
  }
}

const setNewContent = (text: string) => {
  content.value = text
}
const getText = () => {
  editorRef.value.quill.getText()
}

defineExpose({
  setNewContent,
  getText
})
</script>

<style lang="scss">
.quillWrapper {
  line-height: normal;
}
.ql-toolbar {
  background-color: $gray-color-2;
}

.ql-editor {
  min-height: 240px !important;

  // Styles like on marketplace
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin-bottom: 16px;
    font-weight: 400;
    margin-top: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  & b,
  & strong {
    color: #000;
  }

  & p {
    font-size: 15px;
    line-height: 160%;
    margin-bottom: 16px !important;
    min-height: 1px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    position: relative !important;
    line-height: 160%;
    padding: 0 !important;
    margin-bottom: 6px !important;
  }

  ul,
  ol {
    padding-left: 18px !important;
    margin-bottom: 26px !important;
  }

  ol {
    margin-top: 10px;
  }

  ul li:before {
    content: "" !important;
    position: absolute;
    left: 8px;
    top: 8px;
    height: 6px;
    width: 6px;
    border-radius: 50%;
    background-color: #161616;
  }
}
</style>
